import React, { useEffect, useState } from 'react';
import { StepWrapper, StepperControls, StyledCardSection } from '@fnol/library/src/components';
import UserNotification from '@fnol/library/src/components/UserNotification';
import {
    GenericObject,
    ScreenProps,
    common,
    formatPhoneValue,
    getApplicationId,
    getIsWebsite,
    scrollToFirstError,
} from '@fnol/library/src/lib';
import {
    Backdrop,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useApi } from '@fnol/library/src/hooks';
import { getClaimConfirmation, getClaimConfirmationAnonymous } from '../../rpc/actions';
import { legalPersonType, translations } from '../../lib/consts';
import { useFormikContext } from 'formik';
import SuccessScreen from '../SuccessScreen';
import { performance } from 'perf_hooks';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

let data = new FormData();

const ConfirmationScreen = ({ navigator }: ScreenProps) => {
    const { values, setErrors, setTouched, setFieldValue } = useFormikContext<any>();
    const [currentStep, { goBack }] = navigator;

    const hideStepper = () => {
        const allWithClass = Array.from(document.getElementsByClassName('MuiStepper-root'));

        allWithClass.forEach((element: Element) => {
            if (element instanceof HTMLElement) {
                element.style.display = 'none';
            }
        });
    };

    function createData(title: string, data: string) {
        return { title, data };
    }

    const { response: claimConfirmation, fetchMethod: fetchClaimConfirmation } = useApi(
        () => getClaimConfirmation(data),
        hideStepper,
    );

    const { response: claimConfirmationAnonymous, fetchMethod: fetchClaimConfirmationAnonymous } = useApi(
        () => getClaimConfirmationAnonymous(data),
        hideStepper,
    );

    const onCheckBoxChange = (e: any) => {
        setFieldValue('contactAgreement', e.target.checked);
    };

    const toDate = (date: string) => {
        return new Date(date);
    };

    const updDate = `${('0' + toDate(values.dateOfOccurrence).getDate()).slice(-2)}.${(
        '0' +
        (toDate(values.dateOfOccurrence).getMonth() + 1)
    ).slice(-2)}.${toDate(values.dateOfOccurrence).getFullYear()}`;

    const personalEmail =
        values.submitterType === legalPersonType.individual
            ? values.submitterIndividualEmail
            : values.submitterLegalEntityContactEmail;
    const personalMobile =
        values.submitterType === legalPersonType.individual
            ? values.submitterIndividualPhone
            : values.submitterLegalEntityContactPhone;

    const submitter =
        values.submitterType === legalPersonType.individual
            ? `${values.submitterIndividualName} ${values.submitterIndividualLastName}`
            : values.submitterType === legalPersonType.legal
            ? `${values.submitterLegalEntityContactName} ${values.submitterLegalEntityContactLastName}`
            : '';

    const submitterContact =
        values.submitterType === legalPersonType.individual
            ? `${formatPhoneValue(values.submitterIndividualPhone)}${
                  formatPhoneValue(values.submitterIndividualPhone) && values.submitterIndividualEmail ? ', ' : ''
              }${values.submitterIndividualEmail}`
            : values.submitterType === legalPersonType.legal
            ? `${formatPhoneValue(values.submitterLegalEntityContactPhone)}${
                  formatPhoneValue(values.submitterLegalEntityContactPhone) && values.submitterLegalEntityContactEmail
                      ? ', '
                      : ''
              }${values.submitterLegalEntityContactEmail}`
            : '';

    const rows = [
        createData(translations.RISKS, `${values.risk.value}`),
        createData(translations.confirmationScreen.DATE, updDate),
        createData(translations.confirmationScreen.POLICY_SUBMITTER, submitter),
        createData(translations.confirmationScreen.POLICY_SUBMITTER_CONTACT, submitterContact),
    ];

    if (values.numberOfWaitingDays && values.numberOfWaitingDays != '0') {
        rows.push(
            createData(
                translations.confirmationScreen.WAITING_PERIOD,
                translations.confirmationScreen.WAITING_PERIOD_TEXT,
            ),
        );
    }

    const handleSubmit = (): void => {
        if (!claimConfirmation.isLoading && !claimConfirmationAnonymous.isLoading) {
            data = new FormData();
            data.append('stetaTempId', values.tempID);
            data.append('saglasnost', values.contactAgreement);

            if (getIsWebsite(getApplicationId())) {
                setTouched(
                    {
                        oneTimePass: true,
                    },
                    false,
                );

                const existingErrors: GenericObject = {};
                if (values.oneTimePass === '' || values.oneTimePass === null) {
                    existingErrors.oneTimePass = translations.validationMessages.REQUIRED_FIELD;
                    setErrors(existingErrors);
                    scrollToFirstError(existingErrors);
                } else {
                    data.append('kontakt', values.tempPassContact);
                    data.append('sifra', values.oneTimePass);
                    fetchClaimConfirmationAnonymous();
                }
            } else {
                fetchClaimConfirmation();
            }
        }
    };
    const handleGoBack = (): void => {
        goBack();
    };
    return (
        <StepWrapper>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={claimConfirmation.isLoading || claimConfirmationAnonymous.isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <StyledCardSection>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <>
                        <TableContainer sx={{ paddingBottom: 7 }}>
                            <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>
                                            {translations.confirmationScreen.POLICY_NUMBER}
                                        </StyledTableCell>
                                        <StyledTableCell align="left">{values.policyNumber}</StyledTableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {rows.map((row) => (
                                        <StyledTableRow key={row.title}>
                                            <StyledTableCell component="th" scope="row">
                                                {row.title}
                                            </StyledTableCell>
                                            <StyledTableCell align="left">{row.data}</StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography style={{ fontSize: '13px' }}>
                            {translations.confirmationScreen.NOTE_PREMIUM}
                        </Typography>
                        <Typography style={{ fontSize: '13px' }}>
                            {translations.confirmationScreen.NOTE_INTERNATIONAL_SANCTIONS}
                        </Typography>
                        <Typography style={{ fontSize: '13px' }}>
                            {translations.confirmationScreen.PRIVACY}
                            <a
                                href={`${translations.confirmationScreen.PRIVACY_POLICY_URL}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {translations.confirmationScreen.PRIVACY_POLICY_LINK}
                            </a>
                            {translations.FULL_STOP}
                        </Typography>

                        <FormControlLabel
                            name="contactAgreement"
                            id="contactAgreement"
                            control={<Checkbox />}
                            onChange={onCheckBoxChange}
                            label={
                                <Typography style={{ fontSize: '13px' }}>
                                    {translations.confirmationScreen.AGREEMENT}
                                </Typography>
                            }
                            componentsProps={{ typography: { fontSize: '13px' } }}
                        />

                        {getIsWebsite(getApplicationId()) && (
                            <UserNotification
                                tempId={values.tempID}
                                notificationEmail={personalEmail}
                                notificationMobile={personalMobile}
                            />
                        )}
                    </>
                ) : (
                    <SuccessScreen
                        claimNo={
                            claimConfirmation.data.data
                                ? claimConfirmation.data.data
                                : claimConfirmationAnonymous.data.data
                        }
                    />
                )}

                {claimConfirmationAnonymous.error.message && !claimConfirmationAnonymous.isSuccess && (
                    <Typography className="confirmation-message">
                        {claimConfirmationAnonymous.error.response?.data?.match(/^\([0-9]+\)/)
                            ? translations.confirmationScreen.WRONG_PASS
                            : translations.confirmationScreen.CLAIM_ERROR}
                    </Typography>
                )}
                {claimConfirmation.error.message && !claimConfirmation.isSuccess && (
                    <Typography className="confirmation-message">
                        {claimConfirmation.error.response?.data?.match(/^\([0-9]+\)/)
                            ? translations.confirmationScreen.WRONG_PASS
                            : translations.confirmationScreen.CLAIM_ERROR}
                    </Typography>
                )}
            </StyledCardSection>
            <>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <StepperControls
                        onNext={handleSubmit}
                        onNextLabel={common.SEND}
                        onPrevious={handleGoBack}
                        onPreviousLabel={common.PREVIOUS}
                        idNext="confirmationScreenNext"
                        idPrevious="confirmationScreenPrevious"
                    />
                ) : null}
            </>
        </StepWrapper>
    );
};

export default ConfirmationScreen;
